import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, Image as ImageIcon } from "@phosphor-icons/react";
import { adminApi } from "./api";
import ImageUpload from "./ImageUpload";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_MEMBER = { name: "", designation: "", bio: "", photo_id: null, linkedin: "", email: "" };

/**
 * Edits the CMS key `page.our-core-team` which drives /our-core-team page.
 * Structure: { seo_title, meta_description, h1, hero_copy, rows: [ { layout, members: [ {name, designation, bio, photo_id, linkedin, email} ] } ] }
 */
export default function CoreTeamManager() {
  const [data, setData] = useState({ seo_title: "", meta_description: "", h1: "", hero_copy: "", rows: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/site-content`);
        const j = await r.json();
        const raw = j?.page?.["page.our-core-team"];
        const parsed = typeof raw === "string" ? JSON.parse(raw) : (raw || {});
        parsed.rows = parsed.rows || [];
        setData(parsed);
      } catch (e) {
        toast.error("Failed to load team data");
      }
      setLoading(false);
    })();
  }, []);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setMember = (rowIdx, memberIdx, k, v) =>
    setData((d) => {
      const rows = [...d.rows];
      const members = [...(rows[rowIdx].members || [])];
      members[memberIdx] = { ...members[memberIdx], [k]: v };
      rows[rowIdx] = { ...rows[rowIdx], members };
      return { ...d, rows };
    });

  const addRow = () =>
    setData((d) => ({ ...d, rows: [...d.rows, { layout: "solo", members: [{ ...EMPTY_MEMBER }] }] }));

  const removeRow = (idx) => {
    if (!window.confirm("Remove this row?")) return;
    setData((d) => ({ ...d, rows: d.rows.filter((_, i) => i !== idx) }));
  };

  const addMember = (rowIdx) =>
    setData((d) => {
      const rows = [...d.rows];
      const members = [...(rows[rowIdx].members || []), { ...EMPTY_MEMBER }];
      rows[rowIdx] = { ...rows[rowIdx], members, layout: members.length > 1 ? "duo" : "solo" };
      return { ...d, rows };
    });

  const removeMember = (rowIdx, memberIdx) => {
    if (!window.confirm("Remove this member?")) return;
    setData((d) => {
      const rows = [...d.rows];
      const members = (rows[rowIdx].members || []).filter((_, i) => i !== memberIdx);
      rows[rowIdx] = { ...rows[rowIdx], members, layout: members.length > 1 ? "duo" : "solo" };
      return { ...d, rows };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/page-content", { key: "page.our-core-team", value: JSON.stringify(data) });
      toast.success("Team page saved.");
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-[#6B7A8D]">Loading…</div>;

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="core-team-manager">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-[24px]" style={{ color: "#0F2A6E" }}>Core Team (/our-core-team)</h1>
          <p className="mt-1 text-[13px] text-[#6B7A8D]">Edit names, designations, bios and photos. Reorder members with Add/Remove.</p>
        </div>
        <button
          type="button" onClick={save} disabled={saving} data-testid="core-team-save"
          className="h-9 px-4 rounded-md text-white text-[12.5px] font-semibold disabled:opacity-60 inline-flex items-center gap-1.5"
          style={{ background: "#1B6FE0" }}>
          <FloppyDisk size={13} weight="fill" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Page-level fields */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6 space-y-3">
        <div>
          <Label>H1 heading</Label>
          <Input value={data.h1 || ""} onChange={(e) => setField("h1", e.target.value)} className="mt-1" data-testid="core-team-h1" />
        </div>
        <div>
          <Label>Hero paragraph</Label>
          <Textarea value={data.hero_copy || ""} onChange={(e) => setField("hero_copy", e.target.value)} rows={2} className="mt-1" data-testid="core-team-hero" />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>SEO title</Label>
            <Input value={data.seo_title || ""} onChange={(e) => setField("seo_title", e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>Meta description</Label>
            <Input value={data.meta_description || ""} onChange={(e) => setField("meta_description", e.target.value)} className="mt-1" />
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="space-y-6">
        {data.rows.map((row, rIdx) => (
          <div key={rIdx} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`core-team-row-${rIdx}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-semibold text-[15px]" style={{ color: "#0F2A6E" }}>
                Row {String(rIdx + 1).padStart(2, "0")}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => addMember(rIdx)} className="text-[11.5px] px-2 h-7 rounded border border-slate-200 inline-flex items-center gap-1" data-testid={`core-team-add-member-${rIdx}`}>
                  <Plus size={11} weight="bold" /> Add member
                </button>
                <button type="button" onClick={() => removeRow(rIdx)} className="text-[11.5px] px-2 h-7 rounded border border-red-200 text-red-600 inline-flex items-center gap-1" data-testid={`core-team-remove-row-${rIdx}`}>
                  <Trash size={11} weight="bold" /> Remove row
                </button>
              </div>
            </div>

            <div className={`grid gap-5 ${(row.members || []).length > 1 ? "md:grid-cols-2" : "grid-cols-1"}`}>
              {(row.members || []).map((m, mIdx) => (
                <div key={mIdx} className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 space-y-3" data-testid={`core-team-member-${rIdx}-${mIdx}`}>
                  <div className="flex justify-between items-center">
                    <div className="text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: "#1B6FE0" }}>
                      Member {mIdx + 1}
                    </div>
                    {(row.members || []).length > 1 && (
                      <button type="button" onClick={() => removeMember(rIdx, mIdx)} className="text-[10.5px] text-red-500 inline-flex items-center gap-1">
                        <Trash size={10} weight="bold" /> Remove
                      </button>
                    )}
                  </div>
                  <div>
                    <Label className="text-[11.5px]">Photo</Label>
                    <ImageUpload
                      value={m.photo_id}
                      onChange={(id) => setMember(rIdx, mIdx, "photo_id", id)}
                      data-testid={`core-team-photo-${rIdx}-${mIdx}`}
                    />
                  </div>
                  <div>
                    <Label>Full name</Label>
                    <Input value={m.name || ""} onChange={(e) => setMember(rIdx, mIdx, "name", e.target.value)} className="mt-1" data-testid={`core-team-name-${rIdx}-${mIdx}`} />
                  </div>
                  <div>
                    <Label>Designation</Label>
                    <Input value={m.designation || ""} onChange={(e) => setMember(rIdx, mIdx, "designation", e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label>Bio</Label>
                    <Textarea rows={7} value={m.bio || ""} onChange={(e) => setMember(rIdx, mIdx, "bio", e.target.value)} className="mt-1 text-[12.5px]" data-testid={`core-team-bio-${rIdx}-${mIdx}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11.5px]">LinkedIn URL</Label>
                      <Input value={m.linkedin || ""} onChange={(e) => setMember(rIdx, mIdx, "linkedin", e.target.value)} className="mt-1 text-[12px]" placeholder="https://…" />
                    </div>
                    <div>
                      <Label className="text-[11.5px]">Email</Label>
                      <Input value={m.email || ""} onChange={(e) => setMember(rIdx, mIdx, "email", e.target.value)} className="mt-1 text-[12px]" placeholder="name@…" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <button type="button" onClick={addRow} className="w-full h-11 rounded-md border border-dashed border-slate-300 text-[12.5px] font-medium inline-flex items-center justify-center gap-1.5 hover:bg-slate-50" data-testid="core-team-add-row" style={{ color: "#0F2A6E" }}>
          <Plus size={13} weight="bold" /> Add row
        </button>
      </div>
    </div>
  );
}
